// 一次性遷移:本地房間(rooms.ts,localStorage)→ 後端分組(device.group + device_group_order)。
//
// CAM-24 Stage 3c:web 舊有「房間」是純本地偏好。分組改走後端同步後,首次載入時把既有本地
// 房間搬上後端,之後本地房間清空、不再使用。冪等且保守:只有在「本地有房間」且「目前沒有任何
// 裝置帶後端 group」時才跑(避免蓋掉已在別處分好組的使用者),且每個 session 至多跑一次。

import { deviceGroupName } from './groups';
import type { DeviceSummary } from './deviceModel';
import { loadRooms, clearRooms } from './rooms';
import { setDeviceGroup, setDeviceGroupOrder } from '../../lib/api';

/** 遷移計畫:要對哪些裝置設哪個 group,以及要寫入的分組排序(去重、首次出現序)。 */
export interface RoomMigrationPlan {
  assignments: { deviceId: string; group: string }[];
  order: string[];
}

/**
 * 純函式:算出遷移計畫。回 null = 不需遷移。
 * 觸發條件:本地房間 map 非空 AND 目前 devices 沒有任何一台帶後端 group(不與已分組使用者打架)。
 * 只納入「本地有房名」且「存在於目前 devices」的裝置;order = 這些房名的去重首次出現序。
 */
export function planRoomMigration(
  devices: Pick<DeviceSummary, 'device_id' | 'group'>[],
  rooms: Record<string, string>,
): RoomMigrationPlan | null {
  const roomEntries = Object.entries(rooms).filter(([, name]) => name && name.trim());
  if (roomEntries.length === 0) return null;
  // 已有任何後端分組 → 使用者已在別處分過組,不遷移(保守)。
  if (devices.some((d) => deviceGroupName(d) !== null)) return null;

  const present = new Set(devices.map((d) => d.device_id));
  const assignments: { deviceId: string; group: string }[] = [];
  const seen = new Set<string>();
  const order: string[] = [];
  for (const [deviceId, rawName] of roomEntries) {
    if (!present.has(deviceId)) continue;
    const group = rawName.trim();
    assignments.push({ deviceId, group });
    if (!seen.has(group)) {
      seen.add(group);
      order.push(group);
    }
  }
  if (assignments.length === 0) return null;
  return { assignments, order };
}

// 每個 session(頁面生命週期)至多跑一次,避免 devices reload 重觸。以 uid 記錄已嘗試者。
const attempted = new Set<string>();

/** 測試用:重置 session 一次性守衛。 */
export function resetMigrationGuard(): void {
  attempted.clear();
}

/**
 * 執行遷移(若需要)。回傳 true = 有實際寫入(呼叫端應 reload devices)。
 * 守衛:每個 uid 每 session 至多一次;失敗也算已嘗試(不重試整批,避免無限迴圈)。
 */
export async function migrateRoomsToGroups(
  uid: string,
  devices: Pick<DeviceSummary, 'device_id' | 'group'>[],
): Promise<boolean> {
  if (attempted.has(uid)) return false;
  const plan = planRoomMigration(devices, loadRooms(uid));
  if (!plan) return false;
  attempted.add(uid);
  for (const { deviceId, group } of plan.assignments) {
    await setDeviceGroup(deviceId, group);
  }
  await setDeviceGroupOrder(plan.order);
  clearRooms(uid);
  return true;
}
